import { Ticket, Play } from 'lucide-react';
import type { Product } from './types';
import { normalizeProductPricing } from './pricingNormalization';

export const itServiceDeskPlatform: Product = normalizeProductPricing({
  id: 'it-service-desk-platform',
  title: 'IT Service Desk Platform',
  subtitle: 'AI-Powered IT Ticketing, Self-Service Portal & Omnichannel Support',
  description: 'Enterprise-grade IT service desk platform combining AI-powered ticket management, intelligent self-service portals, omnichannel support, and advanced conversation intelligence for BPO, call centres, and IT support teams.',
  icon: Ticket,
  image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'IT & Support',
  status: 'Live',
  businessTypes: ['#bpo', '#call-centre', '#it-support', '#helpdesk', '#customer-service'],
  features: [
    // Omnichannel Ticketing (8 features)
    'Unified Omnichannel Ticketing System (Email, Chat, Phone, Social, Slack, Teams)',
    'AI-Powered Ticket Classification & Intent Detection',
    'Intelligent Ticket Routing Based on Skills, Availability & Expertise',
    'Automated SLA Management with Priority Escalation Alerts',
    'Custom Ticket Forms & Dynamic Field Configuration',
    'Ticket Merging, Splitting & Linking for Complex Issues',
    'Collision Detection for Real-Time Agent Collaboration',
    'Multi-Brand & Multi-Language Ticket Support',

    // AI-Powered Agent Workspace (8 features)
    'Unified Agent Desktop with Customer 360° View',
    'AI Copilot with Smart Reply Suggestions & Macro Recommendations',
    'Real-Time Sentiment Analysis & Customer Mood Detection',
    'Knowledge Base Integration with Contextual Article Suggestions',
    'Automated Ticket Summarization & Conversation History',
    'Side Conversations for Cross-Team Collaboration',
    'Customizable Agent Views, Filters & Saved Searches',
    'Bulk Actions & Workflow Automations for High-Volume Support',

    // Self-Service Portal & Knowledge Base (7 features)
    'Customizable Self-Service Portal with Brand Theming',
    'AI-Powered Knowledge Base with Smart Search',
    'AI Chatbot with Natural Language Understanding',
    'Community Forums & Peer-to-Peer Support',
    'Interactive FAQ Builder with Analytics',
    'Video Tutorial & Multimedia Content Support',
    'Ticket Submission & Status Tracking for End Users',

    // Workflow Automation & Triggers (7 features)
    'Visual Workflow Builder for Custom Automation Rules',
    'Trigger-Based Actions on Ticket Events',
    'Time-Based Automations & SLA Breach Notifications',
    'Automated Ticket Assignment & Load Balancing',
    'Escalation Rules with Multi-Level Hierarchies',
    'Integration Webhooks & API Automation Triggers',
    'Approval Workflows for Change & Service Requests',

    // Analytics & Reporting (6 features)
    'Real-Time Dashboard with Live Ticket Metrics',
    'CSAT, NPS & Customer Effort Score Tracking',
    'Agent Performance Analytics & Productivity Reports',
    'SLA Compliance & Resolution Time Analysis',
    'AI-Powered Trend Detection & Issue Forecasting',
    'Custom Report Builder with Scheduled Exports',

    // Integrations & Apps (6 features)
    'Native Integrations with Slack, Teams & Zoom',
    'CRM Integrations (Salesforce, HubSpot, Dynamics)',
    'ITSM Connectors for Asset & Change Management',
    '1000+ Pre-Built App Marketplace Integrations',
    'REST API & Webhooks for Custom Development',
    'Single Sign-On (SSO) with SAML & OAuth Support',

    // Enterprise Security (8 features)
    'SOC 2 Type II & ISO 27001 Certified Infrastructure',
    'End-to-End Encryption for All Data at Rest & Transit',
    'Role-Based Access Control (RBAC) with Granular Permissions',
    'Multi-Factor Authentication & Hardware Token Support',
    'GDPR, HIPAA & PCI-DSS Compliance Ready',
    'Comprehensive Audit Trails & Access Logging',
    'Data Residency Options with Regional Hosting',
    'IP Whitelisting & Advanced Network Security',
  ],
  pricing: { setup: 28600, monthly: 965 },
  color: 'from-violet-600 to-purple-700',
  demoConfig: {
    type: 'interactive',
    url: '/service-desk/demo',
    title: 'Experience the Complete Platform',
    description: 'Explore our AI-powered IT service desk with ticket management, self-service portal, and advanced analytics',
    buttonText: 'Launch Service Desk Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700',
  },
});

